import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import {
  PencilFill, CheckLg, XLg, PlusCircle, Trash, TrophyFill, ArrowRight,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateCompletedTracksSection } from '../../services/contentService';
import './CompletedTracksBlock.scss';

function defaultEntry() {
  return {
    trackTitle: 'Track name',
    seasonLabel: 'Season 1',
    description: 'Brief description of the challenge and the winning approach.',
    winnerName: 'Winning team',
    linkText: 'Read about it →',
    linkUrl: '',
  };
}

export function defaultCompletedTracksData() {
  return {
    title: 'Completed tracks',
    intro: 'Past challenges and their winning solutions',
    season: 'Season 1 (2024-25)',
    entries: [defaultEntry(), defaultEntry()],
  };
}

// Read-only renderer. Each entry has a grey left accent, a header row with
// the track title on the left and a small season label on the right, a
// description paragraph, a yellow "WINNER" highlight box, and an optional
// "Read about it" link that opens in a new tab.
export function CompletedTracksView({ data }) {
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  return (
    <div className="completed-tracks-block">
      {data?.title ? <h2 className="completed-tracks-block__title">{data.title}</h2> : null}
      {data?.intro ? <p className="completed-tracks-block__intro">{data.intro}</p> : null}
      {data?.season ? (
        <span className="completed-tracks-block__season-pill">{data.season}</span>
      ) : null}

      <div className="completed-tracks-block__entries">
        {entries.map((entry, idx) => (
          <div key={idx} className="completed-tracks-block__entry">
            <div className="completed-tracks-block__entry-header">
              <div className="completed-tracks-block__track-title">{entry.trackTitle}</div>
              {entry.seasonLabel ? (
                <div className="completed-tracks-block__season-label">{entry.seasonLabel}</div>
              ) : null}
            </div>
            {entry.description ? (
              <p className="completed-tracks-block__description">{entry.description}</p>
            ) : null}
            {entry.winnerName ? (
              <div className="completed-tracks-block__winner-box">
                <div className="completed-tracks-block__winner-icon-wrap">
                  <TrophyFill className="completed-tracks-block__winner-icon" />
                </div>
                <div>
                  <div className="completed-tracks-block__winner-label">WINNER</div>
                  <div className="completed-tracks-block__winner-name">{entry.winnerName}</div>
                </div>
              </div>
            ) : null}
            {entry.linkUrl ? (
              <a
                href={entry.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="completed-tracks-block__link"
              >
                {entry.linkText || 'Read more'} <ArrowRight />
              </a>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function CompletedTracksBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultCompletedTracksData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setData(section?.data || defaultCompletedTracksData());
  }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateCompletedTracksSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving completed tracks:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setData(section?.data || defaultCompletedTracksData());
    setIsEditing(false);
  }

  function updateField(field, value) {
    setData((prev) => ({ ...prev, [field]: value }));
  }

  function updateEntry(idx, patch) {
    setData((prev) => {
      const next = { ...prev, entries: [...(prev.entries || [])] };
      next.entries[idx] = { ...next.entries[idx], ...patch };
      return next;
    });
  }

  function addEntry() {
    setData((prev) => ({ ...prev, entries: [...(prev.entries || []), defaultEntry()] }));
  }

  function removeEntry(idx) {
    setData((prev) => {
      const next = { ...prev, entries: [...(prev.entries || [])] };
      next.entries.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="completed-tracks-block completed-tracks-block--editing">
        <Row className="g-2 mb-3">
          <Col md={6}>
            <Form.Label>Section title</Form.Label>
            <Form.Control
              type="text"
              value={data.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>Season pill (optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Season 1 (2024-25)"
              value={data.season || ''}
              onChange={(e) => updateField('season', e.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label>Intro paragraph (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={data.intro || ''}
              onChange={(e) => updateField('intro', e.target.value)}
            />
          </Col>
        </Row>

        {(data.entries || []).map((entry, idx) => (
          <div key={idx} className="completed-tracks-block__editor-entry">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Entry {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeEntry(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={8}>
                <Form.Label>Track title</Form.Label>
                <Form.Control
                  type="text"
                  value={entry.trackTitle || ''}
                  onChange={(e) => updateEntry(idx, { trackTitle: e.target.value })}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Season label (small, right-aligned)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Season 1"
                  value={entry.seasonLabel || ''}
                  onChange={(e) => updateEntry(idx, { seasonLabel: e.target.value })}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={entry.description || ''}
                  onChange={(e) => updateEntry(idx, { description: e.target.value })}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Winner name</Form.Label>
                <Form.Control
                  type="text"
                  value={entry.winnerName || ''}
                  onChange={(e) => updateEntry(idx, { winnerName: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Link text</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Read about it in University of Calgary News"
                  value={entry.linkText || ''}
                  onChange={(e) => updateEntry(idx, { linkText: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Link URL (opens new tab)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="https://…"
                  value={entry.linkUrl || ''}
                  onChange={(e) => updateEntry(idx, { linkUrl: e.target.value })}
                />
              </Col>
            </Row>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addEntry} className="mb-3">
          <PlusCircle /> Add entry
        </Button>

        <div className="completed-tracks-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>
            ) : (
              <><CheckLg /> Save</>
            )}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
            <XLg /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="completed-tracks-block completed-tracks-block--view">
      <CompletedTracksView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            className="edit-btn"
            onClick={() => setIsEditing(true)}
          >
            <PencilFill /> Edit tracks
          </Button>
          <SectionControls
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onDelete}
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
          />
        </div>
      </RoleGuard>
    </div>
  );
}

export default CompletedTracksBlock;
